/** Collection list from the Figma frame: thumb, title, checkbox, Delete, New+. Plus search, sort, filter, drag order. */
import { useEffect, useMemo, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ConflictError, type EntrySummary, type FolderCollection } from '@hotpot/core';
import { Layout, norm } from '../components/Layout.js';
import { ChefHat, ErrorBox, Loading, paths, RepoImage, useConfirm } from '../components/ui.js';
import { useT } from '../i18n.js';
import { formatDate } from '../lib/format.js';
import { Link } from '../router.js';
import { useAsync, useSite, useToast } from '../state.js';
import { errorText } from './Login.js';

type Sort = { field: string; direction: 'asc' | 'desc' } | 'manual' | 'default';

export function CollectionList({ name }: { name: string }) {
  const { t, lang } = useT();
  const site = useSite();
  const toast = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const collection = site.config.collections.find((c) => c.name === name) as FolderCollection | undefined;
  const id = site.handle.site.id;
  const entries = useAsync(() => site.list(name), [name, site.version]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [sort, setSort] = useState<Sort>('default');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [order, setOrder] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelected(new Set());
    setOrder(null);
    setQ('');
    setFilter('all');
    setSort(collection?.order_field ? 'manual' : 'default');
  }, [name, collection?.order_field]);

  const visible = useMemo(() => {
    let list = entries.data ?? [];
    if (filter !== 'all') list = list.filter((e) => (filter === 'hidden' ? e.draft : !e.draft));
    if (q.trim()) {
      const s = norm(q.trim());
      list = list.filter((e) => norm(`${e.title} ${e.subtitle} ${e.slug}`).includes(s));
    }
    if (typeof sort === 'object') {
      list = [...list].sort((a, b) => {
        const av = String(a.data[sort.field] ?? '');
        const bv = String(b.data[sort.field] ?? '');
        const d = av.localeCompare(bv, lang, { numeric: true });
        return sort.direction === 'desc' ? -d : d;
      });
    }
    if (sort === 'manual' && order) {
      const index = new Map(order.map((s, i) => [s, i]));
      list = [...list].sort((a, b) => (index.get(a.slug) ?? 0) - (index.get(b.slug) ?? 0));
    }
    return list;
  }, [entries.data, filter, q, sort, order, lang]);

  if (!collection || collection.type !== 'folder') return null;
  const canDrag = sort === 'manual' && !!collection.order_field && !q.trim() && filter === 'all';

  const sortOptions: { value: string; label: string }[] = [];
  if (collection.order_field) sortOptions.push({ value: 'manual', label: t('sortManual') });
  else sortOptions.push({ value: 'default', label: defaultSortLabel(collection, t) });
  for (const f of collection.sort?.fields ?? []) {
    const field = collection.fields.find((x) => x.name === f);
    const label = field?.label ?? f;
    const isDate = field?.type === 'date' || field?.type === 'datetime';
    sortOptions.push({ value: `${f}:desc`, label: `${label}: ${isDate ? t('newestFirst') : t('za')}` });
    sortOptions.push({ value: `${f}:asc`, label: `${label}: ${isDate ? t('oldestFirst') : t('az')}` });
  }

  const toggle = (slug: string) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setSelected(next);
  };

  const remove = async () => {
    const items = (entries.data ?? []).filter((e) => selected.has(e.slug));
    if (!items.length) return;
    const names = items.slice(0, 3).map((e) => `„${e.title}“`).join(', ') + (items.length > 3 ? ` +${items.length - 3}` : '');
    const ok = await confirm({
      title: t('deleteConfirmTitle', { n: items.length === 1 ? `„${items[0]!.title}“` : `${items.length} × ${collection.label_singular ?? collection.label}` }),
      text: t('deleteConfirmText', { names }),
      ok: t('deleteForever'),
    });
    if (!ok) return;
    setBusy(true);
    try {
      const commit = await site.content.deleteEntries(name, items.map((e) => ({ slug: e.slug, sha: e.sha })), site.author);
      site.afterCommit(commit, site.handle.site.url || undefined);
      setSelected(new Set());
      toast(t('deleted'));
    } catch (e) {
      toast(e instanceof ConflictError ? t('conflictTitle') : errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const saveOrder = async () => {
    if (!order || !entries.data) return;
    setBusy(true);
    try {
      const commit = await site.content.reorder(name, order, entries.data, site.author);
      if (commit) site.afterCommit(commit, site.handle.site.url || undefined);
      setOrder(null);
      toast(t('orderSaved'));
    } catch (e) {
      toast(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const all = visible.length > 0 && visible.every((e) => selected.has(e.slug));

  return (
    <Layout>
      <div className="title-row">
        <div>
          <h1>{collection.label}</h1>
          {collection.description && <p className="desc">{collection.description}</p>}
        </div>
        <div className="actions">
          {collection.delete !== false && (
            <button className="link-btn danger" onClick={remove} disabled={!selected.size || busy}>
              {t('delete')}
              {selected.size > 0 ? ` (${selected.size})` : ''}
            </button>
          )}
          {collection.create !== false && (
            <Link className="btn" to={paths.newEntry(id, name)}>
              {t('new')}
            </Link>
          )}
        </div>
      </div>

      <div className="toolbar">
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchIn', { name: collection.label })} aria-label={t('search')} />
        {sortOptions.length > 1 && (
          <label>
            <span className="muted">{t('sortBy')}</span>
            <select
              className="select"
              value={typeof sort === 'object' ? `${sort.field}:${sort.direction}` : sort}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'manual' || v === 'default') setSort(v);
                else {
                  const [field, direction] = v.split(':');
                  setSort({ field: field!, direction: direction as 'asc' | 'desc' });
                }
              }}
            >
              {sortOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {collection.draft_field && (
          <label>
            <select className="select" value={filter} onChange={(e) => setFilter(e.target.value as never)} aria-label={t('filterAll')}>
              <option value="all">{t('filterAll')}</option>
              <option value="visible">{t('filterPublished')}</option>
              <option value="hidden">{t('filterHidden')}</option>
            </select>
          </label>
        )}
        <span className="grow" />
        {order && (
          <button className="btn" onClick={saveOrder} disabled={busy}>
            {t('saveOrder')}
          </button>
        )}
        {entries.data && <span className="muted">{t('items', { n: visible.length })}</span>}
        {visible.length > 0 && collection.delete !== false && (
          <label>
            <span className="muted">{t('selectAll')}</span>
            <input type="checkbox" className="check" checked={all} onChange={() => setSelected(all ? new Set() : new Set(visible.map((e) => e.slug)))} />
          </label>
        )}
      </div>
      {canDrag && visible.length > 1 && <p className="hint" style={{ margin: '-6px 0 12px' }}>{t('dragToReorder')}</p>}

      {entries.error ? (
        <ErrorBox error={entries.error} onRetry={entries.reload} />
      ) : !entries.data ? (
        <Loading />
      ) : visible.length === 0 ? (
        <div className="empty">
          <ChefHat />
          <p>{q ? t('emptySearch', { q }) : t('emptyCollection')}</p>
          {!q && collection.create !== false && (
            <Link className="btn" to={paths.newEntry(id, name)}>
              {t('new')}
            </Link>
          )}
        </div>
      ) : (
        <Rows
          entries={visible}
          collection={collection}
          siteId={id}
          selected={selected}
          onToggle={toggle}
          draggable={canDrag}
          onReorder={(slugs) => setOrder(slugs)}
          lang={lang}
        />
      )}
      {confirmDialog}
    </Layout>
  );
}

function defaultSortLabel(c: FolderCollection, t: ReturnType<typeof useT>['t']): string {
  const d = c.sort?.default;
  if (!d) return 'A–Z';
  const field = c.fields.find((f) => f.name === d.field);
  const isDate = field?.type === 'date' || field?.type === 'datetime';
  return `${field?.label ?? d.field}: ${d.direction === 'desc' ? (isDate ? t('newestFirst') : t('za')) : isDate ? t('oldestFirst') : t('az')}`;
}

function Rows(props: {
  entries: EntrySummary[];
  collection: FolderCollection;
  siteId: string;
  selected: Set<string>;
  onToggle: (slug: string) => void;
  draggable: boolean;
  onReorder: (slugs: string[]) => void;
  lang: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const slugs = props.entries.map((e) => e.slug);
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    props.onReorder(arrayMove(slugs, slugs.indexOf(String(e.active.id)), slugs.indexOf(String(e.over.id))));
  };
  const rows = props.entries.map((e) => <Row key={e.slug} entry={e} {...props} />);
  if (!props.draggable) return <ul className="rows">{rows}</ul>;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={slugs} strategy={verticalListSortingStrategy}>
        <ul className="rows">{rows}</ul>
      </SortableContext>
    </DndContext>
  );
}

function Row({ entry, collection, siteId, selected, onToggle, draggable, lang }: { entry: EntrySummary; collection: FolderCollection; siteId: string; selected: Set<string>; onToggle: (s: string) => void; draggable: boolean; lang: string }) {
  const { t } = useT();
  const sortable = useSortable({ id: entry.slug, disabled: !draggable });
  const style = draggable ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition } : undefined;
  return (
    <li className={`row${sortable.isDragging ? ' dragging' : ''}`} ref={draggable ? sortable.setNodeRef : undefined} style={style}>
      {draggable && (
        <button className="handle" {...sortable.attributes} {...sortable.listeners} aria-label={`${t('dragToReorder')} ${entry.title}`}>
          ⋮⋮
        </button>
      )}
      <RepoImage className="thumb" path={entry.thumbnail} />
      <Link className="main" to={paths.entry(siteId, collection.name, entry.slug)}>
        <span className="t">{entry.title}</span>
        {(entry.subtitle || entry.draft) && (
          <span className="s">
            {entry.draft && <span style={{ color: 'var(--accent-ink)' }}>● {t('hidden')} </span>}
            {entry.subtitle}
          </span>
        )}
      </Link>
      <span className="meta">{entry.date && <span className="date">{formatDate(entry.date, lang)}</span>}</span>
      {collection.delete !== false && (
        <input type="checkbox" className="check" checked={selected.has(entry.slug)} onChange={() => onToggle(entry.slug)} aria-label={entry.title} />
      )}
    </li>
  );
}
